import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";

const KEY_PINNED = "ui.sidebar.pinned";
const SidebarContext = createContext(null);
const EDGE_PX = 4;
const PANEL_PX = 256;     // w-64        // franja izquierda que abre el panel al llevar el mouse al borde
const HIDE_DELAY_MS = 350;

/**
 * Sidebar estilo Excel: por defecto se auto-oculta y aparece al llevar el
 * mouse al borde izquierdo o sobre el panel; con el "pin" queda fijo.
 *  - pinned   → fijo en el layout (persistido en localStorage)
 *  - visible  → mostrado (fijo, o flotando mientras el mouse esté encima)
 */
export function SidebarProvider({ children }) {
  const [pinned, setPinned] = useState(() => {
    try { return localStorage.getItem(KEY_PINNED) === "1"; } catch { return false; }
  });
  const [hovering, setHovering] = useState(false);
  const hideTimer = useRef(null);

  useEffect(() => {
    try { localStorage.setItem(KEY_PINNED, pinned ? "1" : "0"); } catch { /* noop */ }
  }, [pinned]);

  const cancelHide = useCallback(() => {
    if (hideTimer.current) { clearTimeout(hideTimer.current); hideTimer.current = null; }
  }, []);
  const show = useCallback(() => { cancelHide(); setHovering(true); }, [cancelHide]);
  const hideSoon = useCallback(() => {
    if (hideTimer.current) return;
    hideTimer.current = setTimeout(() => { hideTimer.current = null; setHovering(false); }, HIDE_DELAY_MS);
  }, []);

  // Mouse pegado al borde izquierdo → abrir; lejos del panel → cerrar
  useEffect(() => {
    if (pinned) return undefined;
    const onMove = (e) => {
      if (e.clientX <= EDGE_PX) show();
      else if (e.clientX > PANEL_PX + 24) hideSoon();
    };
    window.addEventListener("mousemove", onMove, { passive: true });
    return () => window.removeEventListener("mousemove", onMove);
  }, [pinned, show, hideSoon]);

  const togglePin = useCallback(() => setPinned((p) => !p), []);
  const pin = useCallback(() => setPinned(true), []);
  const unpin = useCallback(() => { setPinned(false); setHovering(false); }, []);

  const visible = pinned || hovering;
  return (
    <SidebarContext.Provider value={{ pinned, visible, togglePin, pin, unpin, show, hideSoon, cancelHide }}>
      {children}
    </SidebarContext.Provider>
  );
}

export function useSidebar() {
  const ctx = useContext(SidebarContext);
  if (!ctx) throw new Error("useSidebar debe usarse dentro de <SidebarProvider>");
  return ctx;
}
